import React, { useState } from 'react';
import { Typography, Space, Button, message } from 'antd';
import {
  SecurityScanOutlined,
  ReloadOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

// Importações necessárias para o modal de relatório
import CSARelatorioModal from './CSARelatorioModal';
import { useCsaRelatorio } from '../../../hooks/useCsaRelatorio';
import { RelatorioOptions } from './CSARelatorioModal';
import { DashboardCSAData } from '../../../types/dashboard/csa.types';

const { Title, Text } = Typography;

// Interface simplificada sem onExport
interface CSAPageHeaderProps {
  clienteId: string;
  clienteNome: string;
  ultimaAtualizacao: string | null;
  onReload: () => void | Promise<void>;
  dashboardData?: DashboardCSAData;
  loading?: boolean;
}

const CSAPageHeader: React.FC<CSAPageHeaderProps> = ({
  clienteId,
  clienteNome,
  ultimaAtualizacao,
  onReload,
  dashboardData,
  loading = false
}) => {
  // Estado do modal de relatório
  const [relatorioModalVisible, setRelatorioModalVisible] = useState(false);
  
  // Hook para gerar relatórios
  const { 
    gerarRelatorio, 
    loading: loadingRelatorio, 
    error: errorRelatorio,
    progresso 
  } = useCsaRelatorio();

  // FUNÇÃO PARA ABRIR MODAL DE RELATÓRIO
  const handleGerarRelatorio = async () => {
    if (!clienteId) {
      message.error('ID do cliente não fornecido');
      return;
    }
    setRelatorioModalVisible(true);
  };

  // FUNÇÃO PARA FECHAR MODAL
  const handleCloseRelatorioModal = () => {
    setRelatorioModalVisible(false);
  };

  // FUNÇÃO PARA GERAR O RELATÓRIO
  const handleGenerateRelatorio = async (options: RelatorioOptions) => {
    try {
      const success = await gerarRelatorio(clienteId, options, dashboardData);
      
      if (success) {
        message.success('Relatório gerado com sucesso!');
        setRelatorioModalVisible(false);
      } else {
        message.error(errorRelatorio || 'Erro ao gerar relatório');
      }
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      message.error('Erro inesperado ao gerar relatório');
    }
  };

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
              <SecurityScanOutlined style={{ marginRight: 8 }} />
              Cyber Security Assessment (CSA)
            </Title>
            <Text type="secondary">
              Cliente: <Text strong>{clienteNome}</Text>
              {ultimaAtualizacao && (
                <>
                  {' | '}
                  Última atualização: {dayjs(ultimaAtualizacao).format('DD/MM/YYYY HH:mm')}
                </>
              )}
            </Text>
          </div>
          
          <Space>
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={onReload}
              loading={loading}
            >
              Atualizar
            </Button>
            
            {/* BOTÃO PARA ABRIR MODAL DE RELATÓRIO */}
            <Button
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={handleGerarRelatorio}
              loading={loadingRelatorio}
              disabled={loading || !clienteId}
            >
              Gerar Relatório
            </Button>
          </Space>
        </div>
      </div>

      {/* MODAL DE RELATÓRIOS CSA */}
      <CSARelatorioModal
        visible={relatorioModalVisible}
        onClose={handleCloseRelatorioModal}
        onGenerate={handleGenerateRelatorio}
        clienteNome={clienteNome}
        loading={loadingRelatorio}
        hostsDisponiveis={dashboardData?.hostsVulneraveis?.map(h => h.host) || []}
        progresso={progresso}
      />
    </>
  );
};

export default CSAPageHeader;